import * as Sentry from '@sentry/react';
import BigNumber from 'bignumber.js';

import { AnalyticsApi, mainApi, queryFnNotifyWrapper } from 'modules/api';
import { authCacheTags } from 'modules/auth/authCacheTags';
import { DECIMALS_RATIO } from 'modules/common';
import { t } from 'modules/i18n';
import { stakeCacheTags } from 'modules/stake/stakeCacheTags';

export interface IGetStats {
  rate: BigNumber;
  tvl: string;
  apy: number;
  apyBoosted: number;
  apyTotal: number;
  stakers: number;
  epochStartedAt: number;
  iotaUsdPrice: BigNumber;
}

export const { useGetStatsQuery } = mainApi.injectEndpoints({
  endpoints: build => ({
    getStats: build.query<IGetStats, void>({
      queryFn: queryFnNotifyWrapper<void, void, IGetStats>(async () => {
        const api = AnalyticsApi.getInstance();
        try {
          const data = await api.getStats();

          return {
            data: {
              rate: new BigNumber(data.ratio).div(DECIMALS_RATIO),
              tvl: data.tvl,
              apy: data.apy,
              apyBoosted: data.boosted_apy,
              apyTotal: data.apy + data.boosted_apy,
              stakers: data.stakers,
              epochStartedAt: data.epoch_started_at,
              iotaUsdPrice: new BigNumber(data.iota_price),
            },
          };
        } catch (error) {
          const errorMessage = t('notification.failedStats');
          const isError = error instanceof Error;
          const failedStatsError = new Error(
            `${errorMessage}: ${isError ? error?.message : ''}`,
          );
          failedStatsError.stack =
            error instanceof Error ? error?.stack : undefined;

          Sentry.captureException(failedStatsError);
          throw failedStatsError;
        }
      }),
      providesTags: [authCacheTags.account, stakeCacheTags.stake],
    }),
  }),
});
